import React, { useEffect, useState } from 'react';
import { ArrowRight, BookOpen } from 'lucide-react';
import './PlotReadyOfferCard.css';

/**
 * Book Discovery Plot Ready offer (#985 / #1089).
 *
 * Equal doors after depth + substance. Opens Outline, where Generate builds
 * the outline from this talk (2026-10-14: no framework needed). Easy to keep talking.
 * Decline persists so reload does not nag. Re-offer is a backend
 * decision after a new solid thread or much more depth.
 */

export const PLOT_READY_OFFER_HINT =
  'Generate opens when this book has a main character, what they want, and what\'s in the way. '
  + 'Or keep going.';

export const PLOT_READY_KEEP_TALKING = 'Keep going';

export const PLOT_READY_OPEN = 'Build my outline';

const declineListeners = new Set();

export function declineStorageKey(sessionId, messageId) {
  return `brainstormy.plotReadyOffer.declined.${sessionId}.${messageId || 'latest'}`;
}

export function subscribePlotReadyOfferDecline(listener) {
  declineListeners.add(listener);
  return () => declineListeners.delete(listener);
}

export function isPlotReadyOfferDeclined(sessionId, messageId) {
  if (!sessionId) return false;
  try {
    return window.localStorage.getItem(declineStorageKey(sessionId, messageId)) === '1';
  } catch {
    return false;
  }
}

export function markPlotReadyOfferDeclined(sessionId, messageId) {
  if (!sessionId) return;
  try {
    window.localStorage.setItem(declineStorageKey(sessionId, messageId), '1');
  } catch {
    /* private mode / quota — hide in this page either way */
  }
  declineListeners.forEach((listener) => {
    listener(sessionId, messageId);
  });
}

export function shouldShowPlotReadyOffer(offer, sessionId, messageId) {
  if (!offer?.offered) return false;
  if (isPlotReadyOfferDeclined(sessionId, messageId)) return false;
  return true;
}

export default function PlotReadyOfferCard({
  sessionId,
  messageId,
  offer,
  onAccept,
  onDecline,
}) {
  const [hidden, setHidden] = useState(() => isPlotReadyOfferDeclined(sessionId, messageId));

  useEffect(() => {
    if (isPlotReadyOfferDeclined(sessionId, messageId)) {
      setHidden(true);
    }
    return subscribePlotReadyOfferDecline((id, declinedMessageId) => {
      if (id === sessionId && declinedMessageId === messageId) setHidden(true);
    });
  }, [sessionId, messageId]);

  if (hidden) return null;
  const items = Array.isArray(offer?.items) ? offer.items.filter((item) => item && item.name) : [];

  const handleDecline = (event) => {
    event?.preventDefault();
    event?.stopPropagation();
    markPlotReadyOfferDeclined(sessionId, messageId);
    setHidden(true);
    onDecline?.();
  };

  return (
    <div className="plot-ready-offer" role="group" aria-label="Build your outline">
      <div className="plot-ready-offer__lead">
        <span className="plot-ready-offer__hint">{PLOT_READY_OFFER_HINT}</span>
        {items.length > 0 ? (
          <ul className="plot-ready-offer__items" data-testid="plot-ready-offer-items">
            {items.map((item) => (
              <li key={item.id || item.name} data-state={item.done ? 'ready' : 'not-ready'}>
                {item.name}
                {' — '}
                {item.done ? 'Ready' : 'Not ready'}
              </li>
            ))}
          </ul>
        ) : null}
        <button
          type="button"
          className="plot-ready-offer__dismiss"
          onClick={handleDecline}
          aria-label="Dismiss"
          title="Dismiss"
        >
          ×
        </button>
      </div>
      <div className="plot-ready-offer__doors">
        <button
          type="button"
          className="plot-ready-offer__door"
          onClick={() => onAccept?.()}
        >
          <BookOpen className="plot-ready-offer__icon" size={15} />
          <span className="plot-ready-offer__label">{PLOT_READY_OPEN}</span>
          <ArrowRight className="plot-ready-offer__arrow" size={14} />
        </button>
        <button
          type="button"
          className="plot-ready-offer__skip"
          onClick={handleDecline}
        >
          {PLOT_READY_KEEP_TALKING}
        </button>
      </div>
    </div>
  );
}
